import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Mail } from 'lucide-react';
import { fmtDateTime } from '../../lib/utils';
import { HtmlView } from '../RichTextEditor';

const personalize = (html, r) => {
  if (!html || !r) return html;
  const map = { first_name: r.first_name || '', last_name: r.last_name || '', company: r.company || '', email: r.email || '' };
  return html.replace(/\{\{\s*(first_name|last_name|company|email)\s*\}\}/g, (_, k) => map[k]);
};

export function EmailViewDialog({ campaignId, recipientEmail, onClose }) {
  const [campaign, setCampaign] = useState(null);
  const [recip, setRecip] = useState(null);

  useEffect(() => {
    api.get(`/comms/campaigns/${campaignId}`).then((r) => setCampaign(r.data)).catch(() => {});
    api.get(`/comms/campaigns/${campaignId}/recipients`, { params: { limit: 500 } }).then((r) => {
      const match = (r.data.items || []).find((x) => x.email === (recipientEmail || '').toLowerCase());
      setRecip(match || null);
    }).catch(() => {});
  }, [campaignId, recipientEmail]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[88vh] overflow-y-auto" data-testid="email-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4 text-magenta" /> Email Details</DialogTitle>
        </DialogHeader>
        {!campaign ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <>
            <div className="text-sm space-y-1.5 border-b border-slate-100 pb-3">
              <div><span className="text-slate-500 text-xs">Campaign:</span> <span className="font-semibold text-navy">{campaign.name}</span></div>
              <div><span className="text-slate-500 text-xs">Subject:</span> <span className="font-medium text-navy">{personalize(campaign.subject, recip) || '—'}</span></div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                {recipientEmail && <span>To: {recipientEmail}</span>}
                {campaign.sent_at && <span>Sent: {fmtDateTime(campaign.sent_at)}</span>}
                {campaign.created_by_name && <span>By: {campaign.created_by_name}</span>}
              </div>
              {recip && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="email-recipient-status">
                  <span className={`capitalize text-[10px] font-bold px-2 py-0.5 rounded ${['bounced', 'failed', 'complained'].includes(recip.status) ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-700'}`}>
                    {recip.status}
                  </span>
                  {(recip.event_types || []).filter((e) => e !== recip.status).map((e) => (
                    <span key={e} className="capitalize text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">{e}</span>
                  ))}
                  {recip.error && <span className="text-[10px] text-rose-500">{recip.error}</span>}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Complete Email Body</div>
              <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid="email-full-body">
                <HtmlView html={personalize(campaign.html_body, recip)} className="text-sm" />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
